/**
 * API 接口服务
 * 根据 https://api.emushow.com 的接口文档定义
 */

import httpClient, { type ApiResponse } from '@/utils/request'

// ==================== 类型定义 ====================

// 通用分页参数
export interface PageParams {
  page?: number
  pageSize?: number
  current?: number
  size?: number
}

// 通用分页响应
export interface PageResponse<T> {
  list: T[]
  total: number
  page: number
  pageSize: number
}

// 产品信息
export interface Product {
  name: string
  id: string
  icon: string | null
}

// 产品类型
export interface ProductType {
  productTypeName: string
  products: Product[]
}

// 产品列表响应（根据实际接口返回格式）
export interface ProductListResponse {
  result: ProductType[]
  succeed: boolean
  message: string
  requestId: string
}

// ==================== 示例接口（请根据实际 API 文档替换） ====================

/**
 * 用户相关接口
 */
export const userApi = {
  /**
   * 用户登录
   */
  login(username: string, password: string): Promise<ApiResponse<{ token: string; userInfo: any }>> {
    return httpClient.post('/api/user/login', {
      username,
      password,
    })
  },

  /**
   * 获取用户信息
   */
  getUserInfo(): Promise<ApiResponse<any>> {
    return httpClient.get('/api/user/info')
  },

  /**
   * 退出登录
   */
  logout(): Promise<ApiResponse<void>> {
    return httpClient.post('/api/user/logout')
  },
}

// 新闻项接口
export interface NewsItem {
  newsId: string
  title: string
  desc: string
  image: string
  content: string
  dateTime: string
  newsType: string
}

// 新闻列表响应
export interface NewsListResponse {
  items: NewsItem[]
  totalCount: number
}

// 新闻详情接口
export interface NewsDetail extends NewsItem {
  lastNewsId: string
  nextNewsId: string
  statement: string | null
}

/**
 * 新闻/资讯相关接口
 */
export const newsApi = {
  /**
   * 获取新闻列表
   * 接口路径: /api/news/newlist
   * 参数: pageIndex, pageSize
   */
  getNewsList(params?: { pageIndex?: number; pageSize?: number; keyword?: string; category?: string }): Promise<ApiResponse<NewsListResponse>> {
    return httpClient.get('/api/news/newlist', { params })
  },

  /**
   * 获取新闻详情
   * 接口路径: /api/news/newsdetail
   * 参数: newsId (查询参数)
   */
  getNewsDetail(newsId: string | number): Promise<ApiResponse<NewsDetail>> {
    return httpClient.get('/api/news/newsdetail', { params: { newsId } })
  },
}

/**
 * 服务案例相关接口
 */
export const serviceApi = {
  /**
   * 获取服务案例列表
   */
  getCaseList(params?: { provinceId?: string | number; cityId?: string | number }): Promise<ApiResponse<any>> {
    // GET /api/news/getcaselist
    // 可选参数: provinceId / cityId（不传则返回全部）
    return httpClient.get('/api/news/getcaselist', { params })
  },

  /**
   * 获取案例详情
   */
  getCaseDetail(id: string): Promise<ApiResponse<any>> {
    // GET /api/news/getcasedetail?id=xxx
    return httpClient.get('/api/news/getcasedetail', { params: { id } })
  },
}

/**
 * 产品相关接口
 */
export const productApi = {
  /**
   * 获取产品列表
   * 返回格式: { code: 200, message: "success", data: ProductType[], success: true }
   */
  getProductList(params?: PageParams): Promise<ApiResponse<ProductType[]>> {
    return httpClient.get('/api/news/productlist', { params })
  },

  /**
   * 获取产品详情
   */
  getProductDetail(id: string | number): Promise<ApiResponse<any>> {
    return httpClient.get(`/api/news/product?id=${id}`)
  },
}

/**
 * 解决方案相关接口
 */
export const solutionApi = {
  /**
   * 获取解决方案列表
   */
  getSolutionList(params?: PageParams): Promise<ApiResponse<PageResponse<any>>> {
    return httpClient.get('/api/solution/list', { params })
  },

  /**
   * 获取解决方案详情
   */
  getSolutionDetail(id: string | number): Promise<ApiResponse<any>> {
    return httpClient.get(`/api/solution/${id}`)
  },
}

/**
 * 关键词相关接口
 */
export interface KeywordItem {
  tItle: string // 注意：接口返回的字段名是 tItle（大写 I）
  link: string
}

export const keywordApi = {
  /**
   * 获取关键词列表
   * 接口路径: /api/news/keywordlist
   */
  getKeywordList(): Promise<ApiResponse<KeywordItem[]>> {
    return httpClient.get('/api/news/keywordlist')
  },
}

/**
 * 联系/咨询相关接口
 */
export const contactApi = {
  /**
   * 提交合作意向表单
   */
  submitCooperation(data: {
    companyName: string
    email: string
    phone: string
    contactPerson?: string
    requirements?: string
  }): Promise<ApiResponse<void>> {
    return httpClient.post('/api/contact/cooperation', data)
  },

  /**
   * 提交咨询表单
   */
  submitConsultation(data: {
    name: string
    email: string
    phone: string
    message?: string
  }): Promise<ApiResponse<void>> {
    return httpClient.post('/api/contact/consultation', data)
  },

  /**
   * 提交联系我们表单
   * 接口路径: /api/news/contactus
   * 参数: commpany, email, phone
   */
  submitContactUs(data: {
    commpany: string
    email: string
    phone: string
  }): Promise<ApiResponse<any>> {
    return httpClient.post('/api/news/contactus', data)
  },

  /**
   * 提交加盟/合作表单（获取盈利方案）
   * 接口路径: /api/news/joinforms
   * 返回: { result, succeed, message, requestId }
   */
  submitJoinForms(data: {
    investmentBudget: string
    storeLocation: string
    storeArea: string
    city: string
    contactName: string
    contactPhone: string
    inquiryContent: string
  }): Promise<ApiResponse<null>> {
    return httpClient.post('/api/news/joinforms', data)
  },
}

/**
 * 地区相关接口
 */
export const regionApi = {
  /**
   * 获取省份列表
   */
  getProvinces(): Promise<ApiResponse<Array<{ id: number; name: string }>>> {
    // 接口返回格式: { result: [{ id, name }], succeed: true, ... }
    // request.ts 会自动转换为 { code: 200, data: result }
    return httpClient.get('/api/news/getprovince')
  },

  /**
   * 获取城市列表
   */
  getCities(provinceId: string | number): Promise<ApiResponse<Array<{ id: number; name: string }>>> {
    // 传省份 id 获取城市列表（不同后端可能使用不同参数名，这里同时带上 id / provinceId 以兼容）
    return httpClient.get('/api/news/getcity', { params: { id: provinceId, provinceId } })
  },
}

/**
 * SEO 内容接口
 */
export interface SeoContent {
  title: string
  description: string
  keyWords: string // 注意：接口返回的字段名是 keyWords（大写 K）
}

/**
 * SEO 相关接口
 */
export const seoApi = {
  /**
   * 获取 SEO 内容
   * 接口路径: /api/news/getseocontent
   * 参数: url (路由路径，如 'contact', 'cooperation' 等)
   */
  getSeoContent(url: string): Promise<ApiResponse<SeoContent>> {
    return httpClient.get('/api/news/getseocontent', { params: { url } })
  },
}

// ==================== 统一导出 ====================

export default {
  user: userApi,
  news: newsApi,
  service: serviceApi,
  product: productApi,
  solution: solutionApi,
  contact: contactApi,
  region: regionApi,
  keyword: keywordApi,
  seo: seoApi,
}
